import React, { useEffect, useMemo, useState } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { Link } from "react-router-dom";
import {
  createSaleReturn,
  deleteSale,
  getAllSale,
  deleteSaleReturn,
  getSingleSale,
} from "../../services/services";
import Loading from "../../components/loading";
import moment from "moment";
import { toastError, toastSuccess } from "../../utils";
import ReturnModal from "../returns/addReturn";
import PartyCardDetail from "../party/partyCardDetail";
import SearchFilter from "../purchase/SearchFilter";
import Pagination from "../../components/Pagination";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { confirmDelete } from "../../components/confirmDelete";

const Sale = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnItemData, setReturnItemData] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState({});
  const [totalItems, setTotalItems] = useState(0);

  const [saleId, setSaleId] = useState(null);
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        textTransform: "uppercase",
        paddingLeft: "6px",
        paddingRight: "6px",
        minWidth: 0,
      },
    },
    cells: {
      style: {
        paddingLeft: "6px",
        paddingRight: "6px",
        minWidth: 0,
      },
    },
  };

  const fetchSales = async () => {
    if (loading) return;

    try {
      setLoading(true);

      const queryPayload = {
        query: {
          page,
          limit,
          ...searchQuery,
        },
      };

      const response = await getAllSale(queryPayload);
      const res = response.data;

      setSales(res.response?.sales || []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.total || 0);
    } catch (error) {
      toastError("Failed to fetch sales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, [page, limit, searchQuery]);

  const handleSearch = (payload) => {
    const incomingQuery = payload?.query || {};

    const isEmpty =
      !incomingQuery.searchBy &&
      !incomingQuery.fromDate &&
      !incomingQuery.toDate;

    // If clear → reset completely

    if (isEmpty) {
      setSearchQuery({});
      setPage(1);
      return;
    }
    // Otherwise set new filter
    setSearchQuery(incomingQuery);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id, row) => {
    const label = row.referenceType === "saleReturn" ? "Sale Return" : "Sale";

    const name = row.customerId?.name || "customer";
    const invoice = row.invoiceNumber || row._id.slice(-6);

    const isConfirm = await confirmDelete(`${label} - ${name} (${invoice})`);

    if (!isConfirm) return;

    try {
      const response =
        row.referenceType === "saleReturn"
          ? await deleteSaleReturn(id)
          : await deleteSale(id);
      toastSuccess(response.data.message);
      fetchSales();
      if (selectedSale?._id === id) setSelectedSale(null);
    } catch (error) {
      toastError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Error deleting sale or sale return",
      );
    }
  };

  const handleReturnSubmit = async (data) => {
    try {
      const response = await createSaleReturn(data);
      fetchSales();
      setReturnModalOpen(false);
      setReturnItemData(null);
      toastSuccess(response.data.response.message);
    } catch (error) {
      toastError(
        error.response?.data?.response ||
          error.response?.data?.error ||
          "Failed to create sale return",
      );
    }
  };

  const handleSelectSale = async (sale) => {
    try {
      const res = await getSingleSale(sale._id);

      if (res.data.status) {
        const { singleSale, returns } = res.data.response;

        const ledgerLike = {
          ...singleSale,
          party: singleSale.customerId,
          date: singleSale.saleDate,
          referenceId: singleSale._id,
          returns: returns || [],
        };

        setSelectedSale(ledgerLike);
        setSaleId(singleSale.invoiceNumber);
      }
    } catch (error) {
      console.error("Failed to fetch sale", error);
      toast.error("Failed to load sale details");
    }
  };

  /* ---------------- DATATABLE COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        name: "S.No.",
        width: "65px",

        cell: (_, index) => (page - 1) * limit + index + 1,
      },
      {
        name: "Invoice No",
        selector: (row) => row.invoiceNumber,
        sortable: true,
        width: "120px",
      },
      {
        name: "Customer",
        selector: (row) => row.customerId?.name || "No Customer",
        sortable: true,
        width: "115px",
      },
      {
        name: "Total (₹)",
        grow: 0,
        selector: (row) =>
          typeof row.totalAmount === "number"
            ? Math.abs(row.totalAmount).toFixed(2)
            : "N/A",
      },
      {
        name: "Discount (₹)",

        selector: (row) =>
          typeof row.discountAmount === "number"
            ? Math.abs(row.discountAmount).toFixed(2)
            : "N/A",
        width: "120px",
      },
      {
        name: "Total GST (₹)",

        sortable: true,
        width: "140px",
        cell: (row) => {
          let totalGST = 0;

          // Items GST
          (row.items || []).forEach((item) => {
            totalGST += parseFloat(item.gstAmount || 0);
          });

          // Services GST
          (row.services || []).forEach((service) => {
            totalGST += parseFloat(service.gstAmount || 0);
          });

          // Other Expenses GST
          (row.otherExpenses || []).forEach((exp) => {
            const price = parseFloat(exp.price || 0);
            const rate = parseFloat(exp.gstRate || 0);
            const type = exp.taxType || "exclusive";
            if (rate > 0) {
              totalGST +=
                type === "inclusive"
                  ? (price * rate) / (100 + rate)
                  : (price * rate) / 100;
            }
          });

          return totalGST > 0 ? (
            <span className="">{totalGST.toFixed(2)}</span>
          ) : (
            <span className="text-muted">-</span>
          );
        },
      },
      {
        name: "Due (₹)",

        selector: (row) =>
          typeof row.dueAmount === "number"
            ? Math.abs(row.dueAmount).toFixed(2)
            : "N/A",
      },
      {
        name: "Sale Date",
        width: 120,

        selector: (row) => moment(row.saleDate).format("DD MMM, YYYY"),
      },
      {
        name: "Actions",

        // center: true,
        cell: (row) => (
          <div className="d-flex gap-1">
            <i
              className="bi bi-arrow-repeat btn btn-sm btn-light border text-primary"
              title="Return"
              onClick={async (e) => {
                e.stopPropagation();

                const res = await getSingleSale(row._id);
                if (res.data.status) {
                  const fullSale = res.data.response.singleSale;
                  setReturnItemData(fullSale);
                  setReturnModalOpen(true);
                }
              }}
            />
            <i
              className="bi bi-trash btn btn-sm btn-light border text-danger"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row._id, row);
              }}
            />
            <Link to={`/sale-invoice/${row._id}`}>
              <button
                className="btn btn-sm border"
                title="Invoice"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="bi bi-printer" />
              </button>
            </Link>
          </div>
        ),
      },
    ],
    [page, limit],
  );

  return (
    <div id="main">
      <div className="row">
        <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-column flex-md-row">
          <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="custom-title mb-0 text-start">Manage Sales</h4>
            <Link to="/sales/create" className="d-block d-md-none ">
              <button className="btn btn-primary p-2 text-nowrap">
                Create Sale
              </button>
            </Link>
          </div>
          <div className="d-flex align-items-center gap-2 ">
            <SearchFilter
              onSearch={handleSearch}
              showItemName={false}
              showProductFilters={false}
            />
            <Link to="/sales/create" className="d-none d-md-block">
              <button className="btn btn-primary p-2 text-nowrap">
                Create Sale
              </button>
            </Link>
          </div>
        </div>

        <div className="col-lg-12 col-md-12 mb-3">
          <div className="card">
            <div className="card-body p-0 rounded">
              <DataTable
                columns={columns}
                data={sales}
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<Loading />}
                highlightOnHover
                striped
                onRowClicked={handleSelectSale}
                noDataComponent={
                  <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <i className="bi bi-inbox display-1 text-muted"></i>
                    <p className="mb-0 text-center p-4 text-muted">
                      No sales records found
                    </p>{" "}
                  </div>
                }
              />
            </div>
          </div>
          {sales.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              limit={limit}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              loading={loading}
            />
          )}
        </div>

        {selectedSale && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
              style={{ zIndex: 1049 }}
              onClick={() => setSelectedSale(null)}
            />
            <PartyCardDetail
              invoiceNumber={saleId}
              ledgerEntry={selectedSale}
              onClose={() => setSelectedSale(null)}
              onDeleteSuccess={() => {
                fetchSales();
                setSelectedSale(null);
              }}
            />
          </>
        )}
      </div>
      {returnModalOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
          style={{ zIndex: 1040 }}
          onClick={() => setReturnModalOpen(false)}
        >
          <ReturnModal
            isOpen={returnModalOpen}
            onClose={() => {
              setReturnModalOpen(false);
              setReturnItemData(null);
            }}
            onSubmit={handleReturnSubmit}
            purchaseData={returnItemData}
            returnType="sale"
          />
        </div>
      )}
    </div>
  );
};

export default headerLayoutWrapper(Sale);
